/// <reference types="@types/google.maps" />
import type { KeyboardEvent as ReactKeyboardEvent, Ref } from "react";
import { useCallback, useLayoutEffect, useRef } from "react";
import { Stack, TextField } from "@mui/material";
import { usePlacesWidget } from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import { addressFromPlace, type PickedAddress } from "./addressFromPlace.js";

type AddressPickerProps = {
  idPrefix: string;
  value: PickedAddress;
  errors: { address: string | undefined; postcode: string | undefined };
  onChange: (value: PickedAddress) => Promisable<void>;
  /** Empty for no search: the address is then typed in full. */
  placesApiKey: string;
};

const placesOptions: google.maps.places.AutocompleteOptions = {
  // Places charges by the fields asked for; `addressFromPlace` reads no others.
  fields: ["name", "types", "address_components"],
  // Unfiltered, so a named place such as a children's home is offered as well
  // as a house.
  types: [],
  componentRestrictions: { country: "gb" },
};

const AddressFields = ({
  idPrefix,
  value,
  errors,
  onChange,
  search,
}: Omit<AddressPickerProps, "placesApiKey"> & {
  search: { inputRef: Ref<HTMLInputElement> } | null;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={3}>
      <TextField
        id={`${idPrefix}-address`}
        label={t("personPage.homeAddress.address")}
        value={value.address}
        onChange={(event) => {
          void onChange({ ...value, address: event.target.value });
        }}
        inputRef={search?.inputRef}
        // Enter picks the highlighted suggestion; left alone it would also
        // submit the form the field sits in.
        onKeyDown={
          search === null
            ? undefined
            : (event: ReactKeyboardEvent) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }
        }
        error={errors.address !== undefined}
        helperText={
          errors.address ??
          (search === null ? undefined : t("personPage.homeAddress.searchHint"))
        }
        slotProps={{ htmlInput: { autoComplete: "off" } }}
        fullWidth
      />
      <TextField
        id={`${idPrefix}-postcode`}
        label={t("personPage.homeAddress.postcode")}
        value={value.postcode}
        onChange={(event) => {
          void onChange({ ...value, postcode: event.target.value });
        }}
        error={errors.postcode !== undefined}
        helperText={errors.postcode}
        sx={{ maxWidth: 200 }}
      />
    </Stack>
  );
};

const SearchingAddressFields = ({
  placesApiKey,
  ...props
}: AddressPickerProps) => {
  const latest = useRef({
    onChange: props.onChange,
    typed: props.value.address,
  });
  useLayoutEffect(() => {
    latest.current = { onChange: props.onChange, typed: props.value.address };
  });

  // Stable, so the widget attaches its listener once rather than on every
  // keystroke; the newest handler and what was typed are read through a ref.
  const onPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      void latest.current.onChange(
        addressFromPlace(place, latest.current.typed),
      );
    },
    [],
  );

  const { ref } = usePlacesWidget<HTMLInputElement>({
    apiKey: placesApiKey,
    onPlaceSelected,
    options: placesOptions,
  });

  return <AddressFields {...props} search={{ inputRef: ref }} />;
};

/**
 * An address and postcode, typed by hand or, given a Places key, filled by
 * choosing from Google's suggestions as the address is typed. Either part can
 * still be corrected by hand, for a place Google does not know.
 */
export const AddressPicker = (props: AddressPickerProps) =>
  props.placesApiKey === "" ? (
    <AddressFields {...props} search={null} />
  ) : (
    <SearchingAddressFields {...props} />
  );
